import * as React from "react"
import { useCallback, useEffect, useState } from "react"
import { 
    Drawer, 
    Box, 
    Typography, 
    IconButton, 
    Button,
    Avatar,
    Chip,
    TextField,
    InputAdornment,
    CircularProgress,
    Grid
} from "@mui/material"
import { IconSeeker, ImageSeeker } from "@ui-seeker/core"
import { useAppToast } from "@ui-seeker/app"
import { postSubmit } from "@ui-seeker/utils"

import { BACKEND_APIS } from "@constants"
import { editMappingDrawer } from "../@signals"
import SkuOption from "./SkuOption"


export default function EditMappingDrawer({ onMappingUpdated }) {
    const { showToast } = useAppToast()
    const drawerData = editMappingDrawer.value
    const isOpen = drawerData !== null
    
    const [skuOptions, setSkuOptions] = useState([])
    const [isLoading, setIsLoading] = useState(false)
    const [isSaving, setIsSaving] = useState(false)
    const [searchText, setSearchText] = useState('')
    const [searchInput, setSearchInput] = useState('')

    const rawProduct = drawerData?.rawProduct
    const currentSku = drawerData?.currentSku
    const brandId = rawProduct?.id_brand || rawProduct?.brand_id || rawProduct?.brandId || rawProduct?.brandID

    console.log('EditMappingDrawer - drawerData:', drawerData)
    console.log('EditMappingDrawer - rawProduct:', rawProduct)
    console.log('EditMappingDrawer - brandId:', brandId)

    const loadSkus = useCallback(() => {
        if (!brandId) {
            console.log('loadSkus - No brandId, skipping')
            return
        }
        
        console.log('loadSkus - Loading SKUs for brandId:', brandId, 'searchText:', searchText)
        
        setIsLoading(true)
        postSubmit(`${BACKEND_APIS.MATCHERS}/selectors/skus-by-brand/${brandId}`, {
            find: searchText
        })
            .then(res => {
                setSkuOptions(res?.options || [])
            })
            .catch(() => {
                showToast.error('Error loading SKUs')
                setSkuOptions([])
            })
            .finally(() => setIsLoading(false))
    }, [brandId, searchText])

    useEffect(() => {
        if (isOpen && brandId) {
            loadSkus()
        }
    }, [isOpen, brandId, searchText, loadSkus])

    useEffect(() => {
        if (!isOpen) {
            setSkuOptions([])
            setSearchText('')
            setSearchInput('')
        }
    }, [isOpen])

    const handleClose = () => {
        editMappingDrawer.value = null
    }

    const handleSearch = () => {
        setSearchText(searchInput)
    }

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleSearch()
        }
    }

    const handleSelectAndSave = async (skuId) => {
        if (isSaving) return
        
        const selectedSkuData = skuOptions.find(s => s.skuID === skuId)

        setIsSaving(true)
        try {
            await postSubmit(`/api/mapping/sku`, {
                productID: rawProduct?.product_id,
                siteID: rawProduct?.id_site,
                skuID: skuId,
                source: 'sku-management'
            })

            showToast.success('Mapping updated successfully')
            
            if (onMappingUpdated) {
                onMappingUpdated({
                    product_id: rawProduct?.product_id,
                    id_site: rawProduct?.id_site,
                    sku: selectedSkuData ? {
                        id_sku: selectedSkuData.skuID,
                        name: selectedSkuData.skuName,
                        brand: selectedSkuData.brand,
                        image: `${BACKEND_APIS.BACKEND}/sku/image/${selectedSkuData.skuID}`
                    } : null
                })
            }
            
            handleClose()
        } catch (error) {
            showToast.error('Error updating mapping')
        } finally {
            setIsSaving(false)
        }
    }

    const handleRemoveMapping = async () => {
        setIsSaving(true)
        try {
            await postSubmit(`/api/mapping/sku`, {
                productID: rawProduct?.product_id,
                siteID: rawProduct?.id_site,
                skuID: 0,
                source: 'sku-management'
            })

            showToast.success('Mapping removed')
            
            if (onMappingUpdated) {
                onMappingUpdated({
                    product_id: rawProduct?.product_id,
                    id_site: rawProduct?.id_site,
                    sku: null
                })
            }
            
            handleClose()
        } catch (error) {
            showToast.error('Error removing mapping')
        } finally {
            setIsSaving(false)
        }
    }

    return (
        <Drawer
            anchor="right"
            open={isOpen}
            onClose={handleClose}
            PaperProps={{
                sx: { 
                    width: { xs: '100%', sm: 600, md: 700 },
                    zIndex: 1400
                }
            }}
            sx={{
                zIndex: 1400
            }}
        >
            {isOpen && (
                <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ 
                        p: 2, 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'space-between',
                        borderBottom: '1px solid #e5e7eb'
                    }}>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            Edit Mapping
                        </Typography>
                        <IconButton onClick={handleClose}>
                            <IconSeeker icon="xmark" />
                        </IconButton>
                    </Box>

                    <Box sx={{ p: 2, backgroundColor: '#fef3c7', borderBottom: '1px solid #e5e7eb' }}>
                        <Typography variant="subtitle2" sx={{ mb: 1, color: '#92400e' }}>
                            Raw Product
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <ImageSeeker
                                src={rawProduct?.image_url}
                                sx={{ 
                                    width: 100, 
                                    height: 100, 
                                    borderRadius: 1,
                                    border: '1px solid #e5e7eb',
                                    objectFit: 'contain',
                                    backgroundColor: '#fff'
                                }}
                            />
                            <Box sx={{ flex: 1 }}>
                                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                                    {rawProduct?.product_name || '-'}
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                                    <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                                        ID: {rawProduct?.product_id}
                                    </Typography>
                                </Box>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                    Site: {rawProduct?.site}
                                </Typography>
                            </Box>
                        </Box>
                    </Box>

                    {currentSku && (
                        <Box sx={{ p: 2, backgroundColor: '#d1fae5', borderBottom: '1px solid #e5e7eb' }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                <Typography variant="subtitle2" sx={{ color: '#065f46' }}>
                                    Current Mapping
                                </Typography>
                                <Button
                                    size="small"
                                    color="error"
                                    startIcon={<IconSeeker icon="link-slash" />}
                                    onClick={handleRemoveMapping}
                                    disabled={isSaving}
                                >
                                    Remove
                                </Button>
                            </Box>
                            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                                <Avatar
                                    src={`${BACKEND_APIS.BACKEND}/sku/image/${currentSku?.id_sku}`}
                                    variant="rounded"
                                    sx={{ width: 50, height: 50, bgcolor: '#ecfdf5' }}
                                />
                                <Box>
                                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#059669' }}>
                                        {currentSku?.name}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        SKU ID: {currentSku?.id_sku}
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                    )}

                    <Box sx={{ p: 2, borderBottom: '1px solid #e5e7eb' }}>
                        <TextField
                            fullWidth
                            size="small"
                            placeholder="Search SKUs by name, EAN, reference..."
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            onKeyPress={handleKeyPress}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <IconSeeker icon="search" sx={{ color: '#9ca3af' }} />
                                    </InputAdornment>
                                ),
                                endAdornment: searchInput && (
                                    <InputAdornment position="end">
                                        <IconButton size="small" onClick={() => { setSearchInput(''); setSearchText(''); }}>
                                            <IconSeeker icon="xmark" sx={{ fontSize: 14 }} />
                                        </IconButton>
                                    </InputAdornment>
                                )
                            }}
                            helperText="Press Enter to search"
                        />
                    </Box>

                    <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
                        {isLoading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                                <CircularProgress />
                            </Box>
                        ) : skuOptions.length === 0 ? (
                            <Box sx={{ textAlign: 'center', py: 4, color: '#9ca3af' }}>
                                <IconSeeker icon="box-open" sx={{ fontSize: 48, mb: 1 }} />
                                <Typography>No SKUs found for this brand</Typography>
                            </Box>
                        ) : (
                            <Grid container spacing={2}>
                                {skuOptions.map((sku) => (
                                    <Grid item xs={12} key={sku.skuID}>
                                        <SkuOption
                                            sku={sku}
                                            isSelected={currentSku?.id_sku === sku.skuID}
                                            isSaving={isSaving}
                                            onSelect={() => handleSelectAndSave(sku.skuID)}
                                        />
                                    </Grid>
                                ))}
                            </Grid>
                        )}
                    </Box>

                    <Box sx={{ 
                        p: 2, 
                        borderTop: '1px solid #e5e7eb',
                        display: 'flex',
                        gap: 2,
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}>
                        <Typography variant="caption" color="text.secondary">
                            Click on a SKU to save mapping instantly
                        </Typography>
                        <Button
                            variant="outlined"
                            onClick={handleClose}
                            disabled={isSaving}
                        >
                            Close
                        </Button>
                    </Box>
                </Box>
            )}
        </Drawer>
    )
}
